import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2, Upload } from 'lucide-react';
import ActivitiesImportPanel from './ActivitiesImportPanel';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import {
  getActivitiesList,
  saveActivityRow,
  deleteActivityRow,
  type ActivityRow,
  type ActivityRiskLevel,
  type ActivityStatus,
} from '@/services/db/dsrEntry';
import { getDSRDetail } from '@/services/db/dsrDetail';
import PhotoGrid from '@/components/dsr/PhotoGrid';
import { ACTIVITY_RISK_TAGS } from '@/constants/activityRiskTags';

const ACTIVITY_STATUSES: { value: ActivityStatus; label: string }[] = [
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'deferred', label: 'Deferred' },
];

const RISK_LEVELS: { value: ActivityRiskLevel; label: string; bg: string }[] = [
  { value: 'low', label: 'Low', bg: 'bg-mozaic-green' },
  { value: 'medium', label: 'Medium', bg: 'bg-mozaic-orange' },
  { value: 'high', label: 'High', bg: 'bg-mozaic-red' },
];

const RISK_FACTOR_CATEGORIES = [
  { value: 'personnel', label: 'Personnel' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'environment', label: 'Environment' },
  { value: 'process', label: 'Process' },
  { value: 'organizational', label: 'Organizational' },
  { value: 'external', label: 'External' },
];

const entrySchema = z
  .object({
    description: z.string().min(1, 'Description is required').max(500, 'Max 500 characters'),
    status: z.enum(['planned', 'in_progress', 'completed', 'cancelled', 'deferred']),
    risk_level: z.enum(['low', 'medium', 'high']),
    risk_tags: z.array(z.string()),
    risk_justification: z.string().optional(),
    risk_factor_category: z.array(z.string()),
    start_time: z.string().optional(),
    duration_minutes: z.string().optional(),
    crew_size: z.string().optional(),
    is_jsa_required: z.boolean(),
    is_complete: z.boolean(),
    notes: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.risk_level === 'high' && !data.risk_justification?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Justification is required for High risk rating',
        path: ['risk_justification'],
      });
    }
    if (
      (data.risk_level === 'medium' || data.risk_level === 'high') &&
      data.risk_factor_category.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Select at least one risk factor category for Medium/High risk',
        path: ['risk_factor_category'],
      });
    }
  });

type EntryFormValues = z.infer<typeof entrySchema>;

const EMPTY: EntryFormValues = {
  description: '',
  status: 'planned',
  risk_level: 'low',
  risk_tags: [],
  risk_justification: '',
  risk_factor_category: [],
  start_time: '',
  duration_minutes: '',
  crew_size: '',
  is_jsa_required: false,
  is_complete: false,
  notes: '',
};

interface Props {
  dsrId: string;
  isReadOnly?: boolean;
}

export default function ActivitiesForm({ dsrId, isReadOnly }: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'closed' | 'adding' | { editing: ActivityRow }>('closed');
  const [importing, setImporting] = useState(false);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['entry_activities', dsrId],
    queryFn: () => getActivitiesList(dsrId),
    staleTime: 30 * 1000,
  });

  // Photo attachments — read-only display per activity card.
  const { data: detail } = useQuery({
    queryKey: ['entry_dsr_photos', dsrId],
    queryFn: () => getDSRDetail(dsrId),
    staleTime: 60 * 1000,
  });
  const photos = detail?.photoAttachments ?? [];

  const onSaved = () => {
    setMode('closed');
    queryClient.invalidateQueries({ queryKey: ['entry_activities', dsrId] });
    queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this activity?')) return;
    try {
      await deleteActivityRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_activities', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete activity');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  const nextSequence = entries.length > 0
    ? Math.max(...entries.map((e) => e.sequence_order)) + 1
    : 1;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Activities</h2>
        <p className="text-xs text-muted-foreground">
          Work performed during the shift. Risk tags help downstream analytics.
        </p>
      </div>

      {entries.length === 0 && mode === 'closed' && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No activities yet. Add an activity below.
          </CardContent>
        </Card>
      )}

      {entries.map((entry, idx) => (
        <ActivityCard
          key={entry.id}
          entry={entry}
          index={idx}
          photos={photos}
          onEdit={() => setMode({ editing: entry })}
          onDelete={() => onDelete(entry.id)}
          isReadOnly={isReadOnly}
        />
      ))}

      {!isReadOnly && mode !== 'closed' && tenantId && (
        <ActivityEntryForm
          dsrId={dsrId}
          tenantId={tenantId}
          sequenceOrder={
            mode !== 'adding' ? mode.editing.sequence_order : nextSequence
          }
          editing={mode !== 'adding' ? mode.editing : undefined}
          onSaved={onSaved}
          onCancel={() => setMode('closed')}
        />
      )}

      {!isReadOnly && mode === 'closed' && importing && tenantId && (
        <ActivitiesImportPanel
          dsrId={dsrId}
          tenantId={tenantId}
          startSequence={nextSequence}
          onClose={() => setImporting(false)}
          onImported={(count) => {
            setImporting(false);
            queryClient.invalidateQueries({ queryKey: ['entry_activities', dsrId] });
            queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
            alert(`Imported ${count} activit${count === 1 ? 'y' : 'ies'}.`);
          }}
        />
      )}

      {!isReadOnly && mode === 'closed' && !importing && (
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 border-dashed border-mozaic-teal text-mozaic-teal"
            onClick={() => setMode('adding')}
          >
            <Plus className="mr-1 h-4 w-4" /> Add Activity
          </Button>
          <Button
            variant="outline"
            className="border-dashed"
            onClick={() => setImporting(true)}
          >
            <Upload className="mr-1 h-4 w-4" /> Import Activities
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function ActivityCard({
  entry,
  index,
  photos,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  entry: ActivityRow;
  index: number;
  photos: any[];
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  const riskMeta = RISK_LEVELS.find((r) => r.value === entry.risk_level) ?? RISK_LEVELS[0];
  const statusMeta = ACTIVITY_STATUSES.find((s) => s.value === entry.status);
  const tagCount = (entry.risk_tags ?? []).length;

  return (
    <Card className={entry.is_complete ? 'opacity-70' : undefined}>
      <CardContent className="pt-6">
        <div className="mb-2 flex items-start justify-between gap-3">
          <h3
            className={`font-semibold text-mozaic-navy ${
              entry.is_complete ? 'line-through text-muted-foreground' : ''
            }`}
          >
            Activity {index + 1}
          </h3>
          <div className="flex items-center gap-2">
            <Badge className="bg-mozaic-navy text-white">
              {statusMeta?.label ?? entry.status}
            </Badge>
            <Badge className={`${riskMeta.bg} text-white`}>
              {riskMeta.label.toUpperCase()}
            </Badge>
          </div>
        </div>
        <p
          className={`text-sm ${
            entry.is_complete ? 'text-muted-foreground line-through' : 'text-foreground'
          }`}
        >
          {entry.description}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {tagCount > 0 && <span>{tagCount} tag{tagCount === 1 ? '' : 's'}</span>}
          {entry.is_jsa_required && (
            <span className="rounded-full bg-mozaic-orange/15 px-2 py-0.5 text-mozaic-orange">
              JSA
            </span>
          )}
          {entry.crew_size && <span>Crew: {entry.crew_size}</span>}
          {entry.duration_minutes && <span>{entry.duration_minutes} min</span>}
        </div>
        <PhotoGrid photos={photos} entityId={entry.id} />
        {!isReadOnly && (
          <div className="mt-3 flex gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityEntryForm({
  dsrId,
  tenantId,
  sequenceOrder,
  editing,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  sequenceOrder: number;
  editing?: ActivityRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: editing
      ? {
          description: editing.description,
          status: editing.status,
          risk_level: editing.risk_level,
          risk_tags: editing.risk_tags ?? [],
          risk_justification: editing.risk_justification ?? '',
          risk_factor_category: Array.isArray(editing.risk_factor_category)
            ? editing.risk_factor_category
            : editing.risk_factor_category
              ? [editing.risk_factor_category as unknown as string]
              : [],
          start_time: extractTimeHHMM(editing.start_time),
          duration_minutes:
            editing.duration_minutes != null ? String(editing.duration_minutes) : '',
          crew_size: editing.crew_size != null ? String(editing.crew_size) : '',
          is_jsa_required: editing.is_jsa_required,
          is_complete: editing.is_complete,
          notes: editing.notes ?? '',
        }
      : EMPTY,
  });

  const riskLevel = watch('risk_level');
  const isComplete = watch('is_complete');

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await saveActivityRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        sequenceOrder,
        description: values.description,
        status: values.status,
        riskLevel: values.risk_level,
        riskTags: values.risk_tags,
        riskJustification: values.risk_justification || null,
        riskFactorCategory: values.risk_factor_category,
        startTime: values.start_time || null,
        durationMinutes: values.duration_minutes ? parseInt(values.duration_minutes, 10) : null,
        crewSize: values.crew_size ? parseInt(values.crew_size, 10) : null,
        isJsaRequired: values.is_jsa_required,
        isComplete: values.is_complete,
        notes: values.notes || null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save activity');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">{editing ? 'Edit Activity' : 'New Activity'}</h3>

        <div className="space-y-2">
          <FieldLabel required>Description</FieldLabel>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea
                {...field}
                rows={3}
                placeholder="What work was performed?"
              />
            )}
          />
          {errors.description && (
            <p className="text-xs text-mozaic-red">{errors.description.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel required>Status</FieldLabel>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <ChipGroup
                  options={ACTIVITY_STATUSES}
                  value={field.value}
                  onChange={field.onChange}
                  wrap
                />
              )}
            />
          </div>
          <div className="space-y-2">
            <FieldLabel required>Risk Level</FieldLabel>
            <Controller
              control={control}
              name="risk_level"
              render={({ field }) => (
                <div className="flex gap-2">
                  {RISK_LEVELS.map((opt) => {
                    const selected = field.value === opt.value;
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => field.onChange(opt.value)}
                        className={`flex-1 rounded-lg border px-3 py-2 text-xs font-bold uppercase transition-colors ${
                          selected
                            ? `border-transparent ${opt.bg} text-white`
                            : 'border-input bg-background hover:bg-muted'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            />
          </div>
        </div>

        {riskLevel === 'high' && (
          <div className="space-y-2">
            <FieldLabel required>Risk Justification</FieldLabel>
            <Controller
              control={control}
              name="risk_justification"
              render={({ field }) => (
                <Textarea
                  {...field}
                  value={field.value ?? ''}
                  rows={2}
                  placeholder="Why is this rated as high risk?"
                />
              )}
            />
            {errors.risk_justification && (
              <p className="text-xs text-mozaic-red">{errors.risk_justification.message}</p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <FieldLabel required={riskLevel === 'medium' || riskLevel === 'high'}>Risk Factor Category</FieldLabel>
          <Controller
            control={control}
            name="risk_factor_category"
            render={({ field }) => {
              const value = field.value ?? [];
              return (
                <div className="flex flex-wrap gap-2">
                  {RISK_FACTOR_CATEGORIES.map((opt) => {
                    const selected = value.includes(opt.value);
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => {
                          const next = selected
                            ? value.filter((v) => v !== opt.value)
                            : [...value, opt.value];
                          field.onChange(next);
                        }}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                          selected
                            ? 'border-mozaic-teal bg-mozaic-teal text-white'
                            : 'border-input bg-background hover:bg-muted'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              );
            }}
          />
          {errors.risk_factor_category && (
            <p className="text-xs text-mozaic-red">{errors.risk_factor_category.message as string}</p>
          )}
        </div>

        <div className="space-y-2">
          <FieldLabel>Risk Tags</FieldLabel>
          <Controller
            control={control}
            name="risk_tags"
            render={({ field }) => {
              const value = field.value ?? [];
              return (
                <div className="flex flex-wrap gap-2">
                  {ACTIVITY_RISK_TAGS.map((tag) => {
                    const selected = value.includes(tag);
                    return (
                      <button
                        type="button"
                        key={tag}
                        onClick={() => {
                          const next = selected
                            ? value.filter((t) => t !== tag)
                            : [...value, tag];
                          field.onChange(next);
                        }}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                          selected
                            ? 'border-mozaic-navy bg-mozaic-navy text-white'
                            : 'border-input bg-background hover:bg-muted'
                        }`}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              );
            }}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <FieldLabel>Start Time</FieldLabel>
            <Controller
              control={control}
              name="start_time"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''} type="time" />
              )}
            />
          </div>
          <div className="space-y-2">
            <FieldLabel>Duration (min)</FieldLabel>
            <Controller
              control={control}
              name="duration_minutes"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''} type="number" inputMode="numeric" />
              )}
            />
          </div>
          <div className="space-y-2">
            <FieldLabel>Crew Size</FieldLabel>
            <Controller
              control={control}
              name="crew_size"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''} type="number" inputMode="numeric" />
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Controller
            control={control}
            name="is_jsa_required"
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="h-4 w-4"
                />
                JSA Required
              </label>
            )}
          />
        </div>

        <div className="space-y-2">
          <Controller
            control={control}
            name="is_complete"
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="h-4 w-4"
                />
                Activity Complete
              </label>
            )}
          />
        </div>

        {isComplete && (
          <div className="space-y-2">
            <FieldLabel>Completion Notes</FieldLabel>
            <Controller
              control={control}
              name="notes"
              render={({ field }) => (
                <Textarea
                  {...field}
                  value={field.value ?? ''}
                  rows={2}
                  placeholder="Notes about completion…"
                />
              )}
            />
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Update Activity' : 'Save Activity'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
      {required && <span className="ml-1 text-mozaic-red">*</span>}
    </Label>
  );
}

function ChipGroup<T extends string>({
  options,
  value,
  onChange,
  wrap,
}: {
  options: readonly { value: T; label: string }[];
  value: T | '';
  onChange: (v: T) => void;
  wrap?: boolean;
}) {
  return (
    <div className={`flex gap-2 ${wrap ? 'flex-wrap' : ''}`}>
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              selected
                ? 'border-mozaic-teal bg-mozaic-teal text-white'
                : 'border-input bg-background hover:bg-muted'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function extractTimeHHMM(value: string | null): string {
  if (!value) return '';
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) return value.slice(0, 5);
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
